import { getFaceCrops } from '@/services/api';
import { logger } from '@/services/logger';
import { FaceRect } from '@d-id/types';

export interface CropRequest {
    imgUrl: string;
    shouldSkipDetection?: boolean;
}

export interface FaceCropResponse {
    faceCrop?: FaceRect;
    metadata: {
        width: number;
        height: number;
        length: number;
    };
}

export const getPresenterFaceRect = async ({ imgUrl, shouldSkipDetection }: CropRequest): Promise<FaceCropResponse | undefined> => {
    try {
        const res = await getFaceCrops({ sourceUrl: imgUrl, shouldSkipDetection });
        if (res && res.faceCrops && res.faceCrops.length) {
            return { faceCrop: res.faceCrops[0], metadata: res.metadata };
        }

        return { metadata: res.metadata };
    } catch (e) {
        logger.error('Failed to get face crop for presenter', e);
    }
};

const cropImage = async ({
    imgUrl,
    faceRect,
    imgSize,
    resultSize = { width: 250, height: 250 },
    padding = 50,
}: {
    imgUrl: string;
    faceRect?: FaceRect;
    imgSize: { height: number; width: number };
    resultSize?: { width: number; height: number };
    padding?: number;
}): Promise<string> => {
    const canvas = document.createElement('canvas');
    canvas.width = resultSize.width;
    canvas.height = resultSize.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get canvas context');
    }

    const img = new Image();
    img.src = imgUrl;
    img.crossOrigin = 'anonymous';

    return new Promise((resolve, reject) => {
        img.onload = () => {
            if (faceRect) {
                const faceWidth = faceRect.right - faceRect.left;
                const faceHeight = faceRect.bottom - faceRect.top;

                const cropWidth = Math.min(faceWidth + 2 * padding, imgSize.width);
                const cropHeight = Math.min(faceHeight + 2 * padding, imgSize.height);

                const left = Math.max(faceRect.left - padding, 0);
                const top = Math.max(faceRect.top - padding, 0);

                const scaleX = canvas.width / cropWidth;
                const scaleY = canvas.height / cropHeight;
                const scale = Math.max(scaleX, scaleY);

                const offsetX = (canvas.width - cropWidth * scale) / 2;
                const offsetY = (canvas.height - cropHeight * scale) / 2;

                ctx.drawImage(img, left, top, cropWidth, cropHeight, offsetX, offsetY, cropWidth * scale, cropHeight * scale);
            } else {
                const scaleX = canvas.width / img.width;
                const scaleY = canvas.height / img.height;
                const scale = Math.max(scaleX, scaleY);

                const destWidth = img.width * scale;
                const destHeight = img.height * scale;
                const offsetX = (canvas.width - destWidth) / 2;
                const offsetY = (canvas.height - destHeight) / 2;

                ctx.drawImage(img, 0, 0, img.width, img.height, offsetX, offsetY, destWidth, destHeight);
            }
            resolve(canvas.toDataURL());
        };
        img.onerror = reject;
    });
};

export const cropPresenterAvatar = async ({ imgUrl, shouldSkipDetection }: CropRequest) => {
    const faceRect = await getPresenterFaceRect({ imgUrl, shouldSkipDetection });

    if (!faceRect) {
        return null;
    }

    const imgSize = { width: faceRect.metadata.width, height: faceRect?.metadata.height };

    return cropImage({ imgUrl, faceRect: faceRect?.faceCrop, imgSize });
};
